import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BadgeCheck, MapPin } from "lucide-react";
import api from "@/lib/api";
import AIContentBlock from "@/components/AIContentBlock";

export const ArtistCard = ({ a }) => (
  <Link to={`/artists/${a.slug}`} data-testid={`artist-card-${a.slug}`} className="card-lift bg-white rounded-3xl border border-slate-100 overflow-hidden group">
    <div className="h-48 overflow-hidden">
      <img src={a.image} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
    </div>
    <div className="p-4">
      <p className="font-bold text-slate-900 flex items-center gap-1.5">
        {a.name}
        {a.verified_status === "verified" && <BadgeCheck className="w-4 h-4 text-sky-500" title="Verified profile" />}
      </p>
      <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3 text-pink-500" /> {a.place}</p>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {a.genres?.slice(0, 2).map((g) => <span key={g} className="px-2 py-0.5 rounded-full bg-pink-50 text-pink-700 text-[10px] font-bold">{g}</span>)}
        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold">{a.verified_status === "verified" ? "Claimed" : "Editorial"}</span>
      </div>
    </div>
  </Link>
);

export default function Artists() {
  const [artists, setArtists] = useState([]);
  const [countries, setCountries] = useState([]);
  const [params, setParams] = useSearchParams();
  const country = params.get("country") || "";

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    api.get("/artists", { params: country ? { country } : {} }).then((r) => setArtists(r.data)).catch(console.error);
  }, [country]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="artists-page">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-slate-500 mb-3">Artists, Composers &amp; Creators</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900">The People Behind <span className="gradient-text">the Sound.</span></h1>
      <p className="mt-3 text-slate-600 max-w-2xl">Verified and editorially documented profiles connecting creators to their places, traditions and works.</p>

      <AIContentBlock pageKey="artists" topic="Artists, composers and creators of world music" imgQuery="musician performing stage portrait" />

      <div className="flex flex-wrap gap-2 mt-8">
        <button data-testid="artists-filter-all" onClick={() => setParams({})} className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${!country ? "gradient-btn text-white border-transparent" : "bg-white border-slate-200 text-slate-600 hover:border-pink-300"}`}>All Countries</button>
        {countries.map((c) => (
          <button key={c.slug} data-testid={`artists-filter-${c.slug}`} onClick={() => setParams({ country: c.slug })} className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${country === c.slug ? "gradient-btn text-white border-transparent" : "bg-white border-slate-200 text-slate-600 hover:border-pink-300"}`}>
            {c.flag} {c.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
        {artists.map((a) => <ArtistCard key={a.slug} a={a} />)}
      </div>
      {artists.length === 0 && <p className="text-slate-500 mt-10" data-testid="artists-empty">No artists documented for this filter yet.</p>}
    </div>
  );
}
